import { Suspense, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { CardSkeleton } from '@/components/ui/states';
import { useRealtimeConnection } from '@/lib/realtime/socket';
import { useTenantCurrency } from '@/lib/auth/useTenantCurrency';
import { useAppSelector } from '@/app/store';
import { cn } from '@/lib/cn';
import { IncomingCallToast } from '@/features/projects/video/IncomingCallToast';
import { MfaSuggestionToast } from './MfaSuggestionToast';

/** The authenticated application frame: fixed sidebar + header + routed content. */
export function AppShell() {
  // One realtime socket for the whole signed-in session; features subscribe to it.
  useRealtimeConnection();
  // Tenant's Finance currency becomes the default for every money figure; the
  // routed content is keyed on it so a change re-renders (formatting is read at render).
  const currency = useTenantCurrency();
  const collapsed = useAppSelector((s) => s.ui.sidebarCollapsed);
  return (
    <div className="min-h-screen bg-surface">
      <div className="print:hidden">
        <Sidebar />
      </div>
      <div className={cn('transition-[padding] duration-200', collapsed ? 'lg:pl-16' : 'lg:pl-52', 'print:pl-0')}>
        <div className="print:hidden">
          <Header />
        </div>
        <main className="p-4 sm:p-6 print:p-0">
          {/* Route-level code splitting means each page can suspend on first visit. */}
          <Suspense fallback={<PageFallback />}>
            <Outlet key={currency} />
          </Suspense>
        </main>
      </div>
      <IncomingCallToast />
      <MfaSuggestionToast />
    </div>
  );
}

function PageFallback() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  );
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}

/** Consistent page title block — every listing/detail design uses this pattern. */
export function PageHeader({ title, subtitle, actions }: PageHeaderProps) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="font-display text-xl font-extrabold tracking-tight text-ink-900">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-ink-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
